import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, PageHeader, Spinner, Badge, Input, Select, EmptyState } from "@/components/ui";
import { Search, ChevronRight } from "lucide-react";

export default function Members() {
  const [members, setMembers] = useState(null);
  const [search, setSearch] = useState("");
  const [risk, setRisk] = useState("");
  const [plan, setPlan] = useState("");
  const navigate = useNavigate();

  const load = () => {
    api.get("/members", { params: { search, risk, plan } }).then((r) => setMembers(r.data));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, risk, plan]);

  return (
    <div className="vc-fade-up">
      <PageHeader title="Member Registry" subtitle="Search and manage your enrolled population. Sorted by risk acuity." />

      <Card className="p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search by name or MRN…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="member-search" />
          </div>
          <Select value={risk} onChange={(e) => setRisk(e.target.value)} data-testid="filter-risk">
            <option value="">All Risk Levels</option>
            <option value="High">High Risk</option>
            <option value="Rising">Rising Risk</option>
            <option value="Low">Low Risk</option>
          </Select>
          <Select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="filter-plan">
            <option value="">All Plans</option>
            <option value="Tailored Plan">Tailored Plan</option>
            <option value="Medicaid Direct">Medicaid Direct</option>
            <option value="State Funded">State Funded</option>
          </Select>
        </div>
      </Card>

      {members === null ? (
        <Spinner />
      ) : members.length === 0 ? (
        <EmptyState title="No members found" hint="Try adjusting your filters." />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Member</th>
                  <th className="px-4 py-3 font-semibold">MRN</th>
                  <th className="px-4 py-3 font-semibold">Plan</th>
                  <th className="px-4 py-3 font-semibold">Domains</th>
                  <th className="px-4 py-3 font-semibold">County</th>
                  <th className="px-4 py-3 font-semibold text-right">Risk Score</th>
                  <th className="px-4 py-3 font-semibold">Risk</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {members.map((m) => (
                  <tr key={m.id} onClick={() => navigate(`/members/${m.id}`)} className="cursor-pointer hover:bg-muted transition-colors" data-testid={`member-row-${m.mrn}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img src={m.photo_url} alt="" className="h-9 w-9 rounded-full object-cover bg-muted" />
                        <div>
                          <p className="font-medium text-foreground">{m.first_name} {m.last_name}</p>
                          <p className="text-xs text-muted-foreground">DOB {m.dob}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{m.mrn}</td>
                    <td className="px-4 py-3 text-muted-foreground">{m.plan}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {m.domains.slice(0, 2).map((d) => <span key={d} className="text-[10px] rounded bg-slate-100 px-1.5 py-0.5 text-muted-foreground">{d}</span>)}
                        {m.domains.length > 2 && <span className="text-[10px] text-muted-foreground">+{m.domains.length - 2}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{m.county}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">{m.risk_score}</td>
                    <td className="px-4 py-3"><Badge tone={m.risk_level}>{m.risk_level}</Badge></td>
                    <td className="px-4 py-3 text-muted-foreground"><ChevronRight className="h-4 w-4" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
